import { Injectable, computed, inject, signal } from '@angular/core';
import { RegisterSelectorService } from '../register-selector/register-selector.service';
import { StoreSelectorService } from '../store-selector/store-selector.service';
import { CurrentDeviceCredentialsService } from './current-device-credentials.service';
import { StoreOption } from '../store-selector/store-option';
import { RegisterOption } from '../register-selector/register-option';
import { DeviceStatus } from './types/device-status';
import { CashRegisterDeviceChallengePurpose } from '../../cash-drawers/cash-register-device/enums/cash-register-device-challenge-purpose.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { DeviceAuthenticatedRequestDto } from '../../cash-drawers/cash-register-device/dtos/partials/device-authenticated.request.dto';

@Injectable({
  providedIn: 'root',
})
export class CurrentDeviceService {
  #credentials = inject(CurrentDeviceCredentialsService);
  #registerCache = inject(RegisterSelectorService);
  #storeCache = inject(StoreSelectorService);

  #loading = signal(false);
  #initializationPromise: Promise<void> | null = null;
  #status = signal<DeviceStatus>('unverified');

  #store = signal<StoreOption | null>(null);
  #register = signal<RegisterOption | null>(null);

  loading = this.#loading.asReadonly();
  status = this.#status.asReadonly();

  store = this.#store.asReadonly();
  register = this.#register.asReadonly();

  storeId = computed(() => this.#store()?.id);
  registerId = computed(() => this.#register()?.id);

  async init(): Promise<void> {
    if (this.#initializationPromise) {
      await this.#initializationPromise;
      return;
    }

    this.#initializationPromise = this.loadDevice();
    try {
      await this.#initializationPromise;
    } finally {
      this.#initializationPromise = null;
    }
  }

  private async loadDevice(): Promise<void> {
    this.reset();
    this.#loading.set(true);

    try {
      await this.initStoreAndRegister();
      await this.initDeviceStatus();
    } catch (err) {
      console.error(err);
      this.#status.set('verification_failed');
    } finally {
      this.#loading.set(false);
    }
  }

  async reset() {
    this.#status.set('unverified');
    this.#store.set(null);
    this.#register.set(null);
  }

  async executeWithDeviceVerification<T>(
    purpose: CashRegisterDeviceChallengePurpose,
    action: (signedChallenge: DeviceAuthenticatedRequestDto) => Promise<T>,
  ): Promise<T> {
    const signedChallenge = await this.#credentials.getSignedChallenge(purpose);

    try {
      const result = await action(signedChallenge);

      this.#status.set('verified');

      return result;
    } catch (error) {
      if (this.isForbiddenError(error)) {
        this.#status.set('verification_failed');
      }

      throw error;
    }
  }

  private async initStoreAndRegister() {
    await Promise.all([
      this.#storeCache.init(),
      this.#registerCache.init(),
    ]);

    const registerId = await this.#credentials.getRegisteredRegisterId();

    const register = this.#registerCache
      .registers()
      ?.find((o) => o.id === registerId);

    if (!register) {
      console.error(
        'Registered device references a register that could not be found.',
      );
      return;
    }

    const store = this.#storeCache
      .stores()
      ?.find((o) => o.id === register.storeId);

    if (!store) {
      console.error(
        'Registered device references a store that could not be found.',
      );
      return;
    }

    this.#register.set(register);
    this.#store.set(store);
  }

  private async initDeviceStatus() {
    const status = await this.#credentials.verify();
    this.#status.set(status);
  }

  private isForbiddenError(error: unknown): error is HttpErrorResponse {
    return error instanceof HttpErrorResponse && error.status === 403;
  }
}
